import { Box, BoxProps, SxProps, TextField, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

export const DynamicTextFieldControlled: React.FC<{
  text: string;
  setText: (value: string) => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  sx?: SxProps;
}> = ({ text, setText, isEditing, setIsEditing, sx }) => {
  const textStyles = text === "" ? { color: "text.disabled" } : {};
  const displayText = text === "" ? "No Value" : text;
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Box sx={{ width: "100%", height: "100%", ...sx }}>
      {isEditing ? (
        <TextField
          inputRef={inputRef}
          onChange={(e) => setText(e.target.value)}
          value={text}
          multiline
          maxRows={4}
          placeholder="No Value"
          variant="standard"
          sx={{
            width: "100%",
            "& .MuiInputBase-input": {
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
        />
      ) : (
        <Typography
          sx={{
            ...textStyles,
            width: "100%",
            wordBreak: "break-word",
            whiteSpace: "normal",
            marginBottom: "1px",
          }}
        >
          {displayText}
        </Typography>
      )}
    </Box>
  );
};

export const DynamicTextField: React.FC<
  {
    text: string;
    setText: (value: string) => void;
    textSx?: SxProps;
    placeholder?: string;
    disabled?: boolean;
  } & BoxProps
> = ({
  text,
  setText,
  sx,
  textSx,
  placeholder: placeholderProp,
  disabled = false,
  ...props
}) => {
  const [_isEditing, setIsEditing] = useState(false);
  const isEditing = _isEditing && !disabled;
  const textStyles = text === "" ? { color: "text.disabled" } : {};
  const placeholder = placeholderProp ?? "No Value";
  const displayText = text === "" ? placeholder : text;
  const inputRef = useRef<HTMLInputElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const [focus, setFocus] = useState(false);

  useEffect(() => {
    if (focus) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.setSelectionRange(text.length, text.length);
        setFocus(false);
      }, 0);
    }
  }, [focus]);

  useEffect(() => {
    const elem = mainRef.current;
    if (!elem) return;
    const handler = (e: Event) => {
      if (!disabled) {
        setIsEditing(true);
        setFocus(true);
      }
    };
    elem.addEventListener("onEdit", handler);
    return () => elem.removeEventListener("onEdit", handler);
  }, [disabled]);

  return (
    <Box
      ref={mainRef}
      tabIndex={-1}
      sx={{ width: "100%", height: "100%", ...sx }}
      onClick={() => {
        if (!disabled) {
          setIsEditing(true);
          setFocus(true);
        }
      }}
      {...props}
    >
      {isEditing ? (
        <TextField
          inputRef={inputRef}
          onBlur={() => setIsEditing(false)}
          onChange={(e) => setText(e.target.value)}
          value={text}
          multiline
          maxRows={4}
          placeholder={placeholder ?? "No Value"}
          variant="standard"
          sx={{
            width: "100%",
            "& .MuiInputBase-input": {
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
            "& .MuiInputBase-root": {
              padding: 0,
            },
          }}
          InputProps={{
            sx: textSx,
          }}
        />
      ) : (
        <Typography
          sx={{
            ...textStyles,
            width: "100%",
            wordBreak: "break-word",
            whiteSpace: "normal",
            ...textSx,
            // Add these lines:
            maxHeight: "6rem", // Roughly 4 lines (adjust based on your font-size)
            overflowY: "auto",
            display: "block",
          }}
        >
          {displayText}
        </Typography>
      )}
    </Box>
  );
};
